<template>
  <Modal
    :title="modalTitle"
    :disabled="locked || !canSave"
    :saving="saving"
    submit-btn-text="Save Payment Schedule"
    @hide-modal="onHideModal"
    @submit="handleSubmit"
  >
    <div class="spb-modal d-flex flex-column gap-3 min-h-[320px]">
      <p class="text-muted mb-0 spb-modal__intro" data-cy="quote-total">
        Plan target: <strong>{{ formattedTotal }}</strong> —
        <template v-if="locked">
          payment plan is locked because the invoice is finalized.
        </template>
        <template v-else>
          set installment amounts below. The plan locks when the invoice is
          finalized.
        </template>
      </p>

      <div
        v-if="!existingPlan"
        class="d-flex align-items-center gap-2"
        data-cy="template-picker"
      >
        <label class="font-semibold mb-0" for="spb-template-select"
          >Template:</label
        >
        <select
          id="spb-template-select"
          v-model.number="selectedTemplateId"
          class="form-control form-control-sm"
          style="min-width: 320px"
          :disabled="templatesLoading || !templates.length"
          data-cy="template-select"
          @change="seedFromTemplate"
        >
          <option v-if="templatesLoading" :value="null">Loading…</option>
          <option v-else-if="!templates.length" :value="null">
            No templates available
          </option>
          <option v-for="t in templates" :key="t.id" :value="t.id">
            {{ templateLabel(t) }}
          </option>
        </select>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <h6 class="font-semibold mb-0">Payment Installments</h6>
        <div class="spb-modal__toggle" role="group" data-cy="split-mode-toggle">
          <button
            type="button"
            class="spb-modal__toggle-btn"
            :class="{
              'spb-modal__toggle-btn--active': splitMode === 'percentage'
            }"
            :disabled="locked"
            data-cy="split-percentage"
            @click="setSplitMode('percentage')"
          >
            By Percentage
          </button>
          <button
            type="button"
            class="spb-modal__toggle-btn"
            :class="{ 'spb-modal__toggle-btn--active': splitMode === 'amount' }"
            :disabled="locked"
            data-cy="split-amount"
            @click="setSplitMode('amount')"
          >
            By Amount
          </button>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2 text-muted small">
        <span style="width: 100%">Label</span>
        <span style="width: 140px; flex: 0 0 140px; text-align: left">{{
          splitMode === "percentage" ? "%" : "Amount"
        }}</span>
        <span style="width: 90px; flex: 0 0 90px; text-align: left">{{
          splitMode === "percentage" ? "= $" : "= %"
        }}</span>
        <span style="width: 160px; flex: 0 0 160px; text-align: left"
          >Due date</span
        >
        <span style="width: 32px"></span>
      </div>

      <PaymentScheduleInstallmentRow
        v-for="(installment, idx) in visibleInstallments"
        :key="installment.id ?? `new-${idx}`"
        :installment="installment"
        :split-mode="splitMode"
        :removable="!locked && visibleInstallments.length > 1"
        :error="rowError(installment)"
        :due-date-error="dueDateError(installment)"
        :disabled="templatesLoading || locked"
        :min-date="rowMinDate(idx)"
        data-cy="installment-row"
        @remove="removeRow(installment)"
        @update:label="(v) => updateLabel(installment, v)"
        @update:value="(v) => updateValue(installment, v)"
        @update:dueDate="(v) => updateDueDate(installment, v)"
      />

      <ItemAdder
        v-if="!locked"
        slim
        transparent
        text="Add Installment"
        :disabled="templatesLoading"
        data-cy="add-installment"
        @click="addRow"
      />

      <div
        v-if="!locked"
        class="spb-modal__summary mt-3"
        data-cy="allocation-summary"
      >
        <div
          class="d-flex justify-content-between align-items-center mb-1 small"
        >
          <div data-cy="allocated-display">
            Allocated: <strong>{{ allocatedDisplay }}</strong>
          </div>
          <div
            :class="fullyAllocated ? 'text-success' : 'text-yellow-700'"
            data-cy="allocation-status"
          >
            <span v-if="fullyAllocated">✓ Fully allocated</span>
            <span v-else>{{ remainingDisplay }}</span>
          </div>
        </div>
        <div
          class="progress spb-modal__progress"
          role="progressbar"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="progress-bar"
            :class="fullyAllocated ? 'bg-success' : 'bg-warning'"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <div
          v-if="!fullyAllocated && unallocatedWarning"
          class="text-danger small mt-2"
          data-cy="allocation-warning"
        >
          <i class="fas fa-exclamation-triangle mr-1" />{{ unallocatedWarning }}
        </div>
        <div
          v-if="driftAdjustment"
          class="text-muted small fst-italic mt-2"
          data-cy="drift-adjustment-notice"
        >
          <i class="fas fa-info-circle mr-1" />{{ driftAdjustmentCopy }}
        </div>
      </div>
    </div>
    <template #footer>
      <div
        class="modal-footer d-flex justify-content-end align-items-center spb-modal__footer"
      >
        <SeraButton
          type="secondary"
          text="Cancel"
          data-cy="modal-cancel-btn"
          class="mr-2"
          @click="onHideModal"
        />
        <SeraButton
          :disabled="locked || !canSave || saving"
          :saving="saving"
          data-cy="modal-submit-btn"
          @click="handleSubmit"
        >
          <span v-if="!saving">Save Payment Schedule</span>
          <Spinner v-else message="Saving..." />
        </SeraButton>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { trackEvent } from "@/lib/pendo";

import SeraButton from "@/components/Button.vue";
import ItemAdder from "@/components/ItemAdder.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { dollars } from "@/mixins";
import PaymentScheduleInstallmentRow from "./PaymentScheduleInstallmentRow.vue";

interface TemplateInstallment {
  id?: number;
  label: string;
  percentage: number;
  position: number;
}

interface ProgressBillingTemplate {
  id?: number;
  name: string;
  description?: string;
  is_active: boolean;
  system_default: boolean;
  installments: TemplateInstallment[];
}

type SplitMode = "percentage" | "amount";

interface Installment {
  id?: number;
  label: string;
  label_overridden?: boolean;
  value: number;
  calculated_opposite_value: number;
  position: number;
  due_date?: string | null;
  _destroy?: boolean;
}

interface JobPaymentSchedule {
  id: number;
  job_id: number;
  split_mode: SplitMode;
  status: string;
  installments: Installment[];
}

interface JobPaymentScheduleResponse {
  schedule: JobPaymentSchedule | null;
}

const props = withDefaults(
  defineProps<{
    jobId: number;
    invoiceBalanceCents: number;
    existingPlan?: JobPaymentSchedule | null;
    planLocked?: boolean;
  }>(),
  {
    existingPlan: null,
    planLocked: false
  }
);

const locked = computed(() => props.planLocked);

const emit = defineEmits<{
  "hide-modal": [];
  saved: [response: JobPaymentScheduleResponse];
  "needs-refresh": [];
}>();

const store = useStore();

const saving = ref(false);
const defaultNetDays = ref(30);
// eslint-disable-next-line prefer-const
let saveCompleted = false;
const initialSplitMode: SplitMode =
  props.existingPlan?.split_mode ?? "percentage";
const splitMode = ref<SplitMode>(initialSplitMode);

interface DriftAdjustment {
  label: string;
  delta: number;
  mode: SplitMode;
}
const driftAdjustment = ref<DriftAdjustment | null>(null);

const installments = ref<Installment[]>(
  props.existingPlan
    ? coerceInstallments(props.existingPlan, initialSplitMode)
    : defaultInstallments(props.invoiceBalanceCents)
);
if (props.existingPlan && !props.planLocked) recalcAllOpposites();

function coerceInstallments(
  plan: JobPaymentSchedule,
  targetMode: SplitMode
): Installment[] {
  const copies = plan.installments.map((i) => ({ ...i }));
  if (plan.split_mode === targetMode) return copies;
  return copies.map((inst) => ({
    ...inst,
    value: Number(inst.calculated_opposite_value),
    calculated_opposite_value: Number(inst.value)
  }));
}

const templates = ref<ProgressBillingTemplate[]>([]);
const templatesLoading = ref(false);
const selectedTemplateId = ref<number | null>(null);
const userTouched = ref(false);

onMounted(async () => {
  if (props.existingPlan) return;
  templatesLoading.value = true;
  try {
    const [list, config] = await Promise.all([
      store.dispatch("fetchProgressBillingSchedules"),
      store
        .dispatch("fetchProgressBillingConfig")
        .catch(() => null as { default_schedule_id: number | null } | null)
    ]);
    defaultNetDays.value = config?.default_net_days ?? 30;
    templates.value = (list ?? []).filter(
      (t: ProgressBillingTemplate) => t.is_active
    );
    const fallback = templates.value[0]?.id ?? null;
    selectedTemplateId.value = config?.default_schedule_id ?? fallback;
    if (selectedTemplateId.value && !userTouched.value) seedFromTemplate();
    applyDefaultDueDates();
  } finally {
    templatesLoading.value = false;
  }
});

function templateLabel(t: ProgressBillingTemplate): string {
  const summary = t.installments.map((i) => `${i.percentage}%`).join(" / ");
  return summary ? `${t.name} (${summary})` : t.name;
}

function seedFromTemplate(): void {
  const tpl = templates.value.find((t) => t.id === selectedTemplateId.value);
  if (!tpl || !tpl.installments.length) return;
  const existingDueDates = visibleInstallments.value.map((i) => i.due_date);
  splitMode.value = "percentage";
  installments.value = tpl.installments
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((tplInst, idx) => ({
      label: tplInst.label,
      label_overridden: false,
      value: Number(tplInst.percentage),
      calculated_opposite_value: Math.round(
        (Number(tplInst.percentage) / 100) * props.invoiceBalanceCents
      ),
      position: idx,
      due_date: existingDueDates[idx] ?? null
    }));
  applyDefaultDueDates();
}

const modalTitle = computed(() => {
  if (locked.value) return "View Payment Schedule";
  return props.existingPlan
    ? "Edit Payment Schedule"
    : "Configure Payment Schedule";
});

const formattedTotal = computed(() => dollars(props.invoiceBalanceCents));

const driftAdjustmentCopy = computed(() => {
  const a = driftAdjustment.value;
  if (!a) return "";
  const direction = a.delta >= 0 ? "increased" : "reduced";
  const magnitude = Math.abs(a.delta);
  const formatted =
    a.mode === "amount" ? dollars(magnitude) : `${formatPercent(magnitude)}%`;
  return `Adjusted ${a.label} by ${formatted} (${direction}) so the plan totals exactly.`;
});

const visibleInstallments = computed(() =>
  installments.value.filter((i) => !i._destroy)
);

const totalAllocated = computed(() =>
  visibleInstallments.value.reduce((sum, i) => sum + Number(i.value || 0), 0)
);

const target = computed(() =>
  splitMode.value === "percentage" ? 100 : props.invoiceBalanceCents
);

const fullyAllocated = computed(
  () => Math.abs(target.value - totalAllocated.value) < 0.001
);

const hasRowError = computed(() =>
  visibleInstallments.value.some(
    (row) => rowError(row) !== null || dueDateError(row) !== null
  )
);

const canSave = computed(() => fullyAllocated.value && !hasRowError.value);

const progressPercent = computed(() => {
  if (target.value <= 0) return 0;
  const ratio = (totalAllocated.value / target.value) * 100;
  return Math.max(0, Math.min(100, ratio));
});

const allocatedDisplay = computed(() => {
  if (splitMode.value === "percentage") {
    return `${formatPercent(totalAllocated.value)}%`;
  }
  return dollars(totalAllocated.value);
});

const remainingDisplay = computed(() => {
  const gap = target.value - totalAllocated.value;
  if (Math.abs(gap) < 0.001) return "";
  if (gap > 0) {
    return splitMode.value === "percentage"
      ? `${formatPercent(gap)}% remaining`
      : `${dollars(gap)} remaining`;
  }
  return splitMode.value === "percentage"
    ? `${formatPercent(-gap)}% over`
    : `${dollars(-gap)} over`;
});

const unallocatedWarning = computed(() => {
  const gap = target.value - totalAllocated.value;
  if (Math.abs(gap) < 0.001) return "";
  const sign = gap > 0 ? "" : "over by ";
  const absGap = Math.abs(gap);
  const pct =
    splitMode.value === "percentage"
      ? absGap
      : props.invoiceBalanceCents > 0
      ? (absGap / props.invoiceBalanceCents) * 100
      : 0;
  const cents =
    splitMode.value === "percentage"
      ? (absGap / 100) * props.invoiceBalanceCents
      : absGap;
  const verb = gap > 0 ? "still unallocated." : "over the plan target.";
  return `${sign}${formatPercent(pct)}% (${dollars(cents)}) ${verb}`;
});

function formatPercent(n: number): string {
  const tenth = Math.round(n * 10) / 10;
  if (Math.abs(n - tenth) < 0.0001) return tenth.toFixed(1);
  const fixed = n.toFixed(3).replace(/\.?0+$/, "");
  return /\./.test(fixed) ? fixed : `${fixed}.0`;
}

function rowError(row: Installment): string | null {
  if (locked.value) return null;
  const v = Number(row.value);
  if (Number.isNaN(v) || v < 0) return "Value cannot be negative";
  if (splitMode.value === "percentage" && v > 100) {
    return "Cannot exceed 100%";
  }
  if (splitMode.value === "amount" && v > props.invoiceBalanceCents) {
    return "Exceeds invoice balance";
  }
  if (!fullyAllocated.value && v === 0) return "Enter a value";
  return null;
}

function dueDateError(row: Installment): string | null {
  if (locked.value) return null;
  if (!row.due_date) return "Due date is required";
  return null;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextDayIso(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
}

function addDaysIso(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const result = new Date(Date.UTC(y, m - 1, d + days));
  return result.toISOString().slice(0, 10);
}

function applyDefaultDueDates(): void {
  const today = todayIso();
  visibleInstallments.value.forEach((inst, idx) => {
    if (!inst.due_date) {
      const previous = visibleInstallments.value[idx - 1]?.due_date;
      inst.due_date = previous
        ? addDaysIso(previous, defaultNetDays.value)
        : addDaysIso(today, idx * defaultNetDays.value);
    }
  });
}

function rowMinDate(idx: number): string {
  let floor = todayIso();
  for (let i = idx - 1; i >= 0; i--) {
    const earlier = visibleInstallments.value[i].due_date;
    if (!earlier) continue;
    const dayAfter = nextDayIso(earlier);
    if (dayAfter > floor) floor = dayAfter;
    break;
  }
  return floor;
}

function defaultInstallments(totalCents: number): Installment[] {
  const half = Math.round(totalCents / 2);
  return [
    {
      label: "Deposit",
      value: 50,
      calculated_opposite_value: half,
      position: 0,
      label_overridden: false,
      due_date: null
    },
    {
      label: "Final Payment",
      value: 50,
      calculated_opposite_value: totalCents - half,
      position: 1,
      label_overridden: false,
      due_date: null
    }
  ];
}

function autoLabel(index: number, total: number): string {
  if (total <= 1) return "Deposit";
  if (index === 0) return "Deposit";
  if (index === total - 1) return "Final Payment";
  return `Progress Payment ${index}`;
}

function rewriteAutoLabels(): void {
  const living = installments.value.filter((i) => !i._destroy);
  const n = living.length;
  living.forEach((inst, idx) => {
    if (!inst.label_overridden) inst.label = autoLabel(idx, n);
    inst.position = idx;
  });
}

function addRow(): void {
  userTouched.value = true;
  const visible = visibleInstallments.value;
  const lastDate =
    visible.length > 0 ? visible[visible.length - 1].due_date : null;
  const newDueDate = lastDate
    ? addDaysIso(lastDate, defaultNetDays.value)
    : addDaysIso(todayIso(), installments.value.length * defaultNetDays.value);

  installments.value.push({
    label: "",
    value: 0,
    calculated_opposite_value: 0,
    position: installments.value.length,
    label_overridden: false,
    due_date: newDueDate
  });
  rewriteAutoLabels();
  recalcAllOpposites();
}

function removeRow(row: Installment): void {
  userTouched.value = true;
  if (row.id) {
    row._destroy = true;
    installments.value = [...installments.value];
  } else {
    const idx = installments.value.indexOf(row);
    if (idx !== -1) installments.value.splice(idx, 1);
  }
  rewriteAutoLabels();
  recalcAllOpposites();
}

function updateLabel(row: Installment, label: string): void {
  userTouched.value = true;
  row.label = label;
  row.label_overridden = label.trim().length > 0;
}

function updateValue(row: Installment, value: number): void {
  userTouched.value = true;
  row.value = value;
  recalcOpposite(row);
  driftAdjustment.value = null;
}

function updateDueDate(row: Installment, due_date: string | null): void {
  userTouched.value = true;
  row.due_date = due_date;
}

function recalcOpposite(row: Installment): void {
  if (splitMode.value === "percentage") {
    row.calculated_opposite_value = Math.round(
      (Number(row.value) / 100) * props.invoiceBalanceCents
    );
  } else if (props.invoiceBalanceCents > 0) {
    row.calculated_opposite_value = Number(
      ((Number(row.value) / props.invoiceBalanceCents) * 100).toFixed(2)
    );
  } else {
    row.calculated_opposite_value = 0;
  }
}

function recalcAllOpposites(): void {
  installments.value.forEach((row) => recalcOpposite(row));
}

function setSplitMode(mode: SplitMode): void {
  if (mode === splitMode.value) return;
  userTouched.value = true;
  splitMode.value = mode;
  installments.value = installments.value.map((inst) => ({
    ...inst,
    value: inst.calculated_opposite_value,
    calculated_opposite_value: inst.value
  }));
  recalcAllOpposites();
  absorbRoundingDrift();
  trackEvent("spb_split_mode_changed", {
    job_id: props.jobId,
    split_mode: mode
  });
}

function absorbRoundingDrift(): void {
  const living = installments.value.filter((i) => !i._destroy);
  if (living.length === 0) return;
  const target =
    splitMode.value === "percentage" ? 100 : props.invoiceBalanceCents;
  const sum = living.reduce((s, i) => s + Number(i.value || 0), 0);
  const drift = target - sum;
  if (Math.abs(drift) < 0.001) return;
  const largest = living.reduce(
    (max, row) => (Number(row.value) > Number(max.value) ? row : max),
    living[0]
  );
  if (splitMode.value === "amount") {
    largest.value = Math.max(0, Math.round(Number(largest.value) + drift));
  } else {
    largest.value = Math.max(
      0,
      Number((Number(largest.value) + drift).toFixed(2))
    );
  }
  recalcOpposite(largest);
  driftAdjustment.value = {
    label: largest.label || "an installment",
    delta: drift,
    mode: splitMode.value
  };
}

watch(
  () => props.invoiceBalanceCents,
  (newTotal, oldTotal) => {
    if (
      splitMode.value === "amount" &&
      oldTotal > 0 &&
      newTotal > 0 &&
      newTotal !== oldTotal
    ) {
      rescaleAmountsTo(newTotal, oldTotal);
    }
    recalcAllOpposites();
  }
);

function rescaleAmountsTo(newTotal: number, oldTotal: number): void {
  const living = installments.value.filter((i) => !i._destroy);
  if (!living.length) return;
  const ratio = newTotal / oldTotal;
  let allocated = 0;
  living.forEach((row, idx) => {
    if (idx < living.length - 1) {
      row.value = Math.round(Number(row.value) * ratio);
      allocated += row.value;
    } else {
      row.value = Math.max(0, newTotal - allocated);
    }
  });
}

function onHideModal(): void {
  if (!saveCompleted) {
    trackEvent("spb_modal_abandoned", {
      job_id: props.jobId,
      is_new_plan: !props.existingPlan
    });
  }
  emit("hide-modal");
}

async function handleSubmit(): Promise<void> {
  if (locked.value || !canSave.value || saving.value) return;

  saving.value = true;
  try {
    const payload = {
      split_mode: splitMode.value,
      expected_balance_cents: props.invoiceBalanceCents,
      installments_attributes: installments.value.map((i) => ({
        id: i.id,
        label: i.label,
        label_overridden: i.label_overridden ?? false,
        value: i.value,
        calculated_opposite_value: i.calculated_opposite_value,
        position: i.position,
        due_date: i.due_date,
        _destroy: i._destroy
      }))
    };

    const action = props.existingPlan
      ? "updateJobPaymentSchedule"
      : "createJobPaymentSchedule";
    const response = await store.dispatch(action, {
      jobId: props.jobId,
      ...payload
    });
    saveCompleted = true;
    const isNewPlan = !props.existingPlan;
    if (isNewPlan) {
      trackEvent("spb_enabled", {
        job_id: props.jobId
      });
    }
    trackEvent("spb_plan_saved", {
      job_id: props.jobId,
      is_new_plan: isNewPlan,
      split_mode: splitMode.value,
      installments_count: visibleInstallments.value.length,
      due_dates_set: visibleInstallments.value.filter((i) => i.due_date).length,
      due_dates_blank: visibleInstallments.value.filter((i) => !i.due_date)
        .length
    });
    window.notify?.success("Payment schedule saved successfully!");
    emit("saved", response);
    emit("hide-modal");
  } catch (err: any) {
    const status = err?.response?.status;
    const serverMessage = extractErrorMessage(err);
    if (status === 404) {
      window.notify?.error?.("Progress billing is disabled for this tenant.");
    } else if (status === 409) {
      window.notify?.error?.(
        serverMessage ??
          "Invoice balance has changed since you opened the modal — please try again."
      );
      emit("needs-refresh");
      emit("hide-modal");
      return;
    } else {
      window.notify?.error?.(
        serverMessage ?? "Failed to save payment schedule. Please try again."
      );
    }
    window.Sentry?.captureException(err);
    // eslint-disable-next-line no-console
    console.error("Failed to save payment schedule", err);
  } finally {
    saving.value = false;
  }
}

function extractErrorMessage(err: any): string | null {
  const data = err?.response?.data;
  if (!data) return null;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  const baseErrors = data.errors?.base;
  if (Array.isArray(baseErrors) && baseErrors.length) return baseErrors[0];
  return null;
}
</script>

<style lang="scss" scoped>
.spb-modal__intro {
  font-size: 0.95rem;
  line-height: 1.4;
}

.spb-modal__footer {
  border-top: 1px solid var(--light-color, #e9ecef);
  padding-top: 1rem;
}

.spb-modal__summary {
  border-top: 1px solid var(--light-color, #e9ecef);
  padding-top: 0.75rem;
}

.spb-modal__progress {
  height: 8px;
  border-radius: 4px;
}

.spb-modal__toggle {
  display: inline-flex;
  border: 1px solid var(--primary-color, #2563eb);
  border-radius: 6px;
  overflow: hidden;
  background-color: #fff;
}

.spb-modal__toggle > .spb-modal__toggle-btn + .spb-modal__toggle-btn {
  border-left: 1px solid var(--primary-color, #2563eb);
}

.spb-modal__toggle-btn {
  padding: 0.375rem 0.875rem;
  border: 0;
  background-color: transparent;
  color: var(--primary-color, #2563eb);
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(.spb-modal__toggle-btn--active):not(:disabled) {
    background-color: rgba(37, 99, 235, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &--active {
    background-color: var(--primary-color, #2563eb);
    color: #fff;
  }
}
</style>
